import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  createStyles,
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  makeStyles,
} from "@material-ui/core"
import clsx from "clsx"
import React, { FC } from "react"
import { colors } from "../../../utils/theme"

const useStyles = makeStyles((theme) =>
  createStyles({
    button: {
      margin: 0,
      border: 0,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "none",
      "&:focus": {
        outline: "none",
      },
    },
    primaryButton: {
      ...theme.typography.button,
      color: colors.neutral.white,
      height: 50,
      textTransform: "none",
      backgroundColor: colors.secondary.grapefruit,
      borderRadius: 26,
      "&:hover": {
        backgroundColor: colors.secondary.raspberry,
        color: colors.neutral.white,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: colors.secondary.crimson,
        color: colors.neutral.white,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: colors.secondary.grapefruit,
        color: colors.neutral.white,
        opacity: 0.5,
        cursor: "not-allowed",
        boxShadow: "none",
      },
    },
    focusPrimary: {
      backgroundColor: colors.secondary.grapefruit,
      color: colors.neutral.white,
      outline: "none",
      boxShadow:
        `0px 0px 0px 2px ${colors.neutral.white},` +
        `0px 0px 0px 4px ${colors.tertiary.tangerine} !important`,
    },
    secondaryButton: {
      ...theme.typography.button,
      color: colors.neutral.white,
      height: 50,
      textTransform: "none",
      backgroundColor: colors.primary.periwinkle,
      borderRadius: 26,
      "&:hover": {
        backgroundColor: colors.primary.shadow,
        color: colors.neutral.white,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: colors.primary.midnight,
        color: colors.neutral.white,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: colors.primary.periwinkle,
        color: colors.neutral.white,
        opacity: 0.5,
        cursor: "not-allowed",
        boxShadow: "none",
      },
    },
    focusSecondary: {
      backgroundColor: colors.primary.periwinkle,
      color: colors.neutral.white,
      outline: "none",
      boxShadow:
        `0px 0px 0px 2px ${colors.neutral.white},` +
        `0px 0px 0px 4px ${colors.tertiary.tangerine} !important`,
    },
    ghostButton: {
      ...theme.typography.h6,
      padding: 0,
      height: 19,
      width: "fit-content",
      color: colors.primary.periwinkle,
      backgroundColor: "transparent",
      textTransform: "none",
      borderRadius: 4,
      "&:hover": {
        backgroundColor: "transparent",
        color: colors.primary.shadow,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: "transparent",
        color: colors.primary.midnight,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: "transparent",
        color: colors.primary.periwinkle,
        opacity: 0.5,
        cursor: "not-allowed",
        boxShadow: "none",
      },
    },
    focusGhost: {
      backgroundColor: "transparent",
      color: colors.primary.midnight,
      outline: "none",
      boxShadow: `0px 0px 0px 2px ${colors.tertiary.tangerine} !important`,
    },
    primaryPortalButton: {
      ...theme.typography.button,
      height: 48,
      borderRadius: 32,
      textTransform: "none",
      backgroundColor: colors.primary.ice,
      color: colors.neutral.denim,
      fontWeight: 400,
      "&:hover": {
        backgroundColor: colors.primary.mist,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: colors.primary.periwinkle,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: colors.primary.periwinkle,
        opacity: 0.5,
        cursor: "not-allowed",
        boxShadow: "none",
      },
    },
    focusPrimaryPortal: {
      backgroundColor: colors.primary.ice,
      outline: "none",
      boxShadow:
        `0px 0px 0px 2px ${colors.neutral.white},` +
        `0px 0px 0px 4px ${colors.tertiary.tangerine} !important`,
    },
    iconButton: {
      height: 48,
      width: 48,
      borderRadius: "50%",
      backgroundColor: colors.primary.ice,
      "&:hover": {
        backgroundColor: colors.primary.cloud,
        boxShadow: "none",
        outline: "none",
      },
      "&:active": {
        backgroundColor: colors.primary.ice,
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: colors.primary.ice,
        opacity: 0.5,
        cursor: "not-allowed",
        boxShadow: "none",
      },
    },
    focusIcon: {
      backgroundColor: colors.primary.mist,
      boxShadow: "none",
      outline: "none",
    },
    liteButton: {
      backgroundColor: colors.neutral.white,
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      padding: theme.spacing(0, 1),
      color: colors.neutral.denim,
      height: 44,
      width: "fit-content",
      textTransform: "none",
      "&:hover": {
        backgroundColor: colors.primary.cloud,
        boxShadow: "none",
        outline: "none",
      },
      "&:disabled": {
        backgroundColor: colors.neutral.white,
        opacity: 0.5,
      },
    },
    liteSmallButton: {
      fontSize: 16,
    },
    focusLite: {
      backgroundColor: colors.primary.ice,
    },
    focusLiteSmall: {
      backgroundColor: colors.primary.ice,
    },
  })
)

export type ButtonProps = {
  customVariant:
    | "primary"
    | "secondary"
    | "ghost"
    | "tertiary"
    | "primary-portal"
    | "lite"
    | "lite-small"
    | "icon"
} & MuiButtonProps

const Button: FC<ButtonProps> = ({ customVariant, ...props }) => {
  const classes = useStyles()
  return (
    <MuiButton
      {...props}
      className={clsx(
        classes.button,
        customVariant === "primary"
          ? classes.primaryButton
          : customVariant === "secondary"
          ? classes.secondaryButton
          : customVariant === "ghost"
          ? classes.ghostButton
          : customVariant === "lite"
          ? classes.liteButton
          : customVariant === "lite-small"
          ? clsx(classes.liteButton, classes.liteSmallButton)
          : customVariant === "primary-portal"
          ? classes.primaryPortalButton
          : undefined,
        props.className
      )}
      focusVisibleClassName={
        customVariant === "primary"
          ? classes.focusPrimary
          : customVariant === "secondary"
          ? classes.focusSecondary
          : customVariant === "ghost"
          ? classes.focusGhost
          : customVariant === "lite"
          ? classes.focusLite
          : customVariant === "lite-small"
          ? classes.focusLiteSmall
          : customVariant === "primary-portal"
          ? classes.focusPrimaryPortal
          : undefined
      }
    >
      {props.children}
    </MuiButton>
  )
}

export const PrimaryButton: FC<MuiButtonProps> = (props) => (
  <Button {...props} customVariant="primary" variant="contained" />
)

export const SecondaryButton: FC<MuiButtonProps> = (props) => (
  <Button {...props} customVariant="secondary" variant="contained" />
)

export const LiteButton: FC<MuiButtonProps> = (props) => (
  <Button {...props} customVariant="lite" variant="contained" />
)

export const LiteSmallButton: FC<MuiButtonProps> = (props) => (
  <Button {...props} customVariant="lite-small" variant="contained" />
)

export const PrimaryPortalButton: FC<MuiButtonProps> = (props) => (
  <Button {...props} customVariant="primary-portal" variant="contained" />
)

export const IconButton: FC<MuiIconButtonProps> = (props) => {
  const classes = useStyles()
  return (
    <MuiIconButton
      {...props}
      className={classes.iconButton}
      focusVisibleClassName={classes.focusIcon}
    />
  )
}

export const GhostButton: FC<MuiButtonProps> = (props) => {
  const classes = useStyles()
  return (
    <button {...props} className={clsx(classes.button, classes.ghostButton)} />
  )
}

export default Button
